import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube, ShieldCheck, FileText } from 'lucide-react';
import { siteData } from '../data.js';
import { useCmsSettings } from '../lib/cms.js';

export default function Footer() {
  const settings = useCmsSettings(siteData);
  return (
    <footer className="bg-slate-900 text-slate-300 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
          <div className="md:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center mb-6">
              <img 
                src={siteData.logo} 
                alt={settings.name} 
                className="h-[85px] w-auto shrink-0 hover:scale-110 transition-transform duration-300"
              />
            </Link>
            <p className="text-slate-400 max-w-md mb-8 leading-relaxed text-sm md:text-base">
              Vi arbejder for dem, der hver dag passer på Danmark og os danskere.
              <br /><br />
              For dem, der står i front, når andre har brug for hjælp.
              <br /><br />
              Tak For Din Indsats er en 100 % frivilligt drevet forening, bygget på fællesskab, støtte og ønsket om, at ingen skal stå alene med følgerne af deres indsats.
            </p>
            <div className="flex gap-4">
              <a href={siteData.socials.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-red-600 hover:text-white transition-all" title="Facebook">
                <Facebook size={20} />
              </a>
              <a href={siteData.socials.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-red-600 hover:text-white transition-all" title="Instagram">
                <Instagram size={20} />
              </a>
              <a href={siteData.socials.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-red-600 hover:text-white transition-all" title="LinkedIn">
                <Linkedin size={20} />
              </a>
              <a href={siteData.socials.youtube} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-red-600 hover:text-white transition-all" title="YouTube">
                <Youtube size={20} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">Hurtige links</h4>
            <ul className="space-y-4">
              {siteData.navigation.map((item) => {
                const isExternal = item.external || item.href.startsWith('http');
                return (
                  <li key={item.name}>
                    {isExternal ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-red-500 transition-colors"
                        title="Tak For Din Indsats"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link to={item.href} className="hover:text-red-500 transition-colors">
                        {item.name}
                      </Link>
                    )}
                  </li>
                );
              })}
              <li>
                <Link 
                  to="/dokumenter-og-regnskab" 
                  className="hover:text-red-500 transition-colors flex items-center gap-2 text-slate-400 hover:text-white"
                >
                  <FileText size={16} className="text-red-500 shrink-0" />
                  <span>Dokumenter og regnskab</span>
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">Kontakt</h4>
            <ul className="space-y-4">
              {settings.contact.emails.map((email) => (
                <li key={email} className="flex items-start gap-3">
                  <Mail className="text-red-500 shrink-0 mt-1" size={18} />
                  <a href={`mailto:${email}`} className="break-all hover:text-red-500 transition-colors">
                    {email}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-3">
                <MapPin className="text-red-500 shrink-0 mt-1" size={18} />
                <span>Hele Danmark</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <ShieldCheck className="text-red-500 shrink-0" size={18} />
                <span>CVR-nr: <span className="font-semibold text-white">{settings.contact.cvr}</span></span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {settings.name}. Alle rettigheder forbeholdes.</p>
          <p className="mt-2 text-xs">Drevet af frivillige kræfter for Danmarks helte.</p>
        </div>
      </div>
    </footer>
  );
}
